<template>
  <div>
    <div class="group-toolbar">
      <span>Наборы комнат: {{ groups.length }}</span>
      <a-button
        v-if="editable"
        :disabled="!freeRooms.length"
        title="Добавить объект"
        aria-label="Добавить объект"
        @click="add">
        <plus-outlined />
      </a-button>
    </div>
    <a-empty
      v-if="!groups.length"
      description="Объекты пока не выбраны" />
    <a-card
      v-for="group in groups"
      :key="group.id"
      size="small"
      class="room-group">
      <template #title>
        <span :title="roomNames(group)">{{ roomSummary(group) }}</span>
        <a-tag
          :color="group.purpose === 'default' ? 'blue' : 'orange'"
          class="purpose-tag">
          {{ group.purpose === 'default' ? 'Дефолтные' : 'Обязательные' }}
        </a-tag>
      </template>
      <template
        v-if="editable"
        #extra>
        <a-button
          size="small"
          title="Редактировать"
          aria-label="Редактировать"
          @click="edit(group)">
          <edit-outlined />
        </a-button>
        <a-button
          size="small"
          danger
          title="Удалить набор"
          aria-label="Удалить набор"
          @click="remove(group)">
          <delete-outlined />
        </a-button>
      </template>
      <div class="selected-objects">
        <div
          v-for="item in group.objects"
          :key="keyOf(item)"
          class="selected-object">
          <img
            v-if="item.asset?.preview_url"
            :src="item.asset.preview_url"
            loading="lazy"
            alt="">
          <div>
            <strong>{{ item.asset?.name || item.asset_uuid }}</strong>
            <div class="muted">
              {{ labels[item.asset_category] }}{{ slotName(item) }}
            </div>
          </div>
        </div>
      </div>
    </a-card>
    <room-object-group-modal
      v-if="open"
      ref="modal"
      :open="open"
      :group="editing"
      :room-types="selectableRooms"
      :occupied-groups="occupiedGroups"
      @close="open = false"
      @save="save" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import { onBeforeRouteLeave } from 'vue-router';
import { Modal, message } from 'ant-design-vue';
import { httpClient } from '@/httpClient';
import { buildRoomGroup, groupObjects, keyOf } from '@/utils/roomObjectGroups';
import RoomObjectGroupModal from './RoomObjectGroupModal.vue';

const props = defineProps({ items: { type: Array, default: () => [] }, editable: { type: Boolean, default: false } });
const emit = defineEmits(['update:items']);
const roomTypes = ref([]);
const selectableRooms = computed(() => roomTypes.value.filter((room) => room.is_service === false));
const open = ref(false);
const editing = ref(null);
const modal = ref(null);
const groups = computed(() => groupObjects(props.items));
const labels = {
  furniture: 'Мебель', material: 'Материал', aperture: 'Проём', molding: 'Молдинг',
};
const slots = {
  wall: 'Стены', floor: 'Пол', ceiling: 'Потолок', door: 'Двери', window: 'Окна',
};
const slotName = (item) => {
  const slot = item.surface || item.aperture_type || item.asset?.aperture_type;
  return slot ? ` · ${slots[slot] || slot}` : '';
};
const occupiedGroups = computed(() => groups.value.filter((group) => group.id !== editing.value?.id));
const freeRooms = computed(() => selectableRooms.value.filter((room) => !groups.value
  .some((group) => group.purpose === 'required' && (group.rooms.includes(null) || group.rooms.includes(room.id)))
  || !groups.value.some((group) => group.purpose === 'default'
    && (group.rooms.includes(null) || group.rooms.includes(room.id)))));
const roomNames = (group) => (group.rooms.includes(null) ? 'Все комнаты'
  : group.rooms.map((id) => roomTypes.value.find((room) => room.id === id)?.name || `Комната #${id}`).join(', '));
const roomSummary = (group) => (group.rooms.length > 4 && !group.rooms.includes(null)
  ? `${group.rooms.length} типов комнат: ${roomNames(group).split(', ').slice(0, 3).join(', ')}…` : roomNames(group));
const add = () => { editing.value = null; open.value = true; };
const edit = (group) => { editing.value = group; open.value = true; };
const remove = (group) => Modal.confirm({
  title: 'Удалить набор объектов?',
  content: roomNames(group),
  okText: 'Удалить',
  cancelText: 'Отменить',
  onOk: () => emit('update:items', props.items.filter((item) => !group.entries.includes(item))),
});
const save = ({ rooms, objects }) => {
  const id = editing.value?.sourceId || window.crypto.randomUUID();
  const previous = editing.value?.entries || [];
  const retained = props.items.filter((item) => !previous.includes(item));
  const targetRooms = rooms.length === selectableRooms.value.length ? [null] : rooms;
  emit('update:items', [...retained, ...buildRoomGroup(id, targetRooms, objects)]);
  open.value = false;
};
onBeforeRouteLeave(async () => !open.value || modal.value.confirmClose());
onMounted(async () => {
  try {
    const { data } = await httpClient.get('/api/plans/room_types');
    roomTypes.value = data;
  } catch (_) { message.error('Не удалось загрузить типы комнат'); }
});
</script>

<style scoped>
.group-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.room-group { margin-bottom: 14px; }
.room-group .ant-btn { margin-left: 8px; }
.purpose-tag { margin-left: 12px; }
.selected-objects { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 12px; }
.selected-object {
  display: flex; gap: 10px; align-items: center; padding: 8px; background: #fafafa; border-radius: 6px;
}
.selected-object img { width: 60px; height: 50px; object-fit: contain; flex: none; }
.selected-object strong { overflow-wrap: anywhere; }
.muted { color: #8c8c8c; font-size: 12px; }
</style>
